import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  DocumentReference,
} from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { AuthStore } from '../auth/auth.store';
import { ExpenseClaim, ExpenseClaimStatus, ExpenseReceipt } from '../models/expense.model';

@Injectable({ providedIn: 'root' })
export class ExpenseService {
  private readonly firestore = inject(Firestore);
  private readonly storage = inject(Storage);
  private readonly authStore = inject(AuthStore);

  private readonly _myClaims = signal<ExpenseClaim[]>([]);
  private readonly _pendingApprovals = signal<ExpenseClaim[]>([]);
  private readonly _reimbursementQueue = signal<ExpenseClaim[]>([]);
  private readonly _loading = signal(false);

  readonly myClaims = this._myClaims.asReadonly();
  readonly pendingApprovals = this._pendingApprovals.asReadonly();
  readonly reimbursementQueue = this._reimbursementQueue.asReadonly();
  readonly loading = this._loading.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) throw new Error('No user logged in');
    return id;
  }

  private get userEmail(): string {
    const email = this.authStore.user()?.email;
    if (!email) throw new Error('No user email');
    return email.toLowerCase();
  }

  async loadMyClaims(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/expenseClaims`);
      const q = query(ref, where('userId', '==', this.userId), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      this._myClaims.set(snap.docs.map((d) => this.mapClaim(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async loadPendingApprovals(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/expenseClaims`);
      const role = this.authStore.userRole();

      if (role === 'hr_admin' || role === 'finance_admin') {
        const q = query(ref, where('status', '==', 'submitted'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        this._pendingApprovals.set(snap.docs.map((d) => this.mapClaim(d.id, d.data())));
        return;
      }

      const approverEmployeeId = await this.getEmployeeIdForUser();
      if (!approverEmployeeId) {
        this._pendingApprovals.set([]);
        return;
      }

      const directQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', approverEmployeeId),
        orderBy('createdAt', 'desc')
      );
      const unassignedNullQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', null),
        orderBy('createdAt', 'desc')
      );
      const unassignedEmptyQuery = query(
        ref,
        where('status', '==', 'submitted'),
        where('approverEmployeeId', '==', ''),
        orderBy('createdAt', 'desc')
      );

      const [directSnap, unassignedNullSnap, unassignedEmptySnap] = await Promise.all([
        getDocs(directQuery),
        getDocs(unassignedNullQuery),
        getDocs(unassignedEmptyQuery),
      ]);

      const direct = directSnap.docs.map((d) => this.mapClaim(d.id, d.data()));
      const unassignedDocs = [...unassignedNullSnap.docs, ...unassignedEmptySnap.docs];
      const backfilled = await this.backfillApprovalsForManager(unassignedDocs, approverEmployeeId);

      const combined = [...direct, ...backfilled];
      if (combined.length === 0) {
        const reconciled = await this.reconcileExpensesForManager(approverEmployeeId);
        this._pendingApprovals.set(reconciled);
        return;
      }

      this._pendingApprovals.set(
        combined.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      );
    } finally {
      this._loading.set(false);
    }
  }

  async loadReimbursementQueue(): Promise<void> {
    this._loading.set(true);
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/expenseClaims`);
      const q = query(ref, where('status', '==', 'approved'), orderBy('approvedAt', 'desc'));
      const snap = await getDocs(q);
      this._reimbursementQueue.set(snap.docs.map((d) => this.mapClaim(d.id, d.data())));
    } finally {
      this._loading.set(false);
    }
  }

  async submitClaim(data: {
    category: ExpenseClaim['category'];
    amount: number;
    currency: string;
    expenseDate: Date;
    merchant?: string | null;
    paymentMethod?: string | null;
    description?: string | null;
    receipts: File[];
  }): Promise<string> {
    const claimId = crypto.randomUUID();
    const approverEmployeeId = await this.getManagerEmployeeId();
    const receipts = await this.uploadReceipts(claimId, data.receipts);

    const claimRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/expenseClaims/${claimId}`
    );

    await setDoc(claimRef, {
      userId: this.userId,
      category: data.category,
      amount: data.amount,
      currency: data.currency,
      expenseDate: data.expenseDate,
      merchant: data.merchant ?? null,
      paymentMethod: data.paymentMethod ?? null,
      description: data.description ?? null,
      receipts,
      status: 'submitted',
      approverId: null,
      approverEmployeeId,
      approvedAt: null,
      reimbursedAt: null,
      rejectionReason: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await this.loadMyClaims();
    return claimId;
  }

  async approveClaim(claim: ExpenseClaim): Promise<void> {
    await this.updateStatus(claim, 'approved');
    await this.loadPendingApprovals();
  }

  async rejectClaim(claim: ExpenseClaim, reason: string): Promise<void> {
    await this.updateStatus(claim, 'rejected', reason);
    await this.loadPendingApprovals();
  }

  async markReimbursed(claim: ExpenseClaim): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/expenseClaims/${claim.id}`);
    await updateDoc(ref, {
      status: 'reimbursed',
      reimbursedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await this.loadReimbursementQueue();
  }

  private async updateStatus(
    claim: ExpenseClaim,
    status: ExpenseClaimStatus,
    rejectionReason?: string
  ): Promise<void> {
    const ref = doc(this.firestore, `tenants/${this.tenantId}/expenseClaims/${claim.id}`);
    await updateDoc(ref, {
      status,
      approverId: this.userId,
      approvedAt: status === 'approved' ? serverTimestamp() : null,
      rejectionReason: status === 'rejected' ? rejectionReason ?? null : null,
      updatedAt: serverTimestamp(),
    });
  }

  private async uploadReceipts(claimId: string, files: File[]): Promise<ExpenseReceipt[]> {
    if (!files || files.length === 0) return [];

    const uploads = files.map(async (file) => {
      const fileRef = ref(
        this.storage,
        `tenants/${this.tenantId}/expenses/${claimId}/${crypto.randomUUID()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      return { name: file.name, url };
    });

    return Promise.all(uploads);
  }

  private mapClaim(id: string, data: Record<string, unknown>): ExpenseClaim {
    return {
      id,
      userId: data['userId'] as string,
      category: data['category'] as ExpenseClaim['category'],
      amount: data['amount'] as number,
      currency: (data['currency'] as string) ?? 'USD',
      expenseDate: (data['expenseDate'] as Timestamp)?.toDate() ?? new Date(),
      merchant: (data['merchant'] as string | null) ?? null,
      paymentMethod: (data['paymentMethod'] as string | null) ?? null,
      description: (data['description'] as string | null) ?? null,
      receipts: (data['receipts'] as ExpenseReceipt[]) ?? [],
      status: data['status'] as ExpenseClaimStatus,
      approverId: (data['approverId'] as string | null) ?? null,
      approverEmployeeId: (data['approverEmployeeId'] as string | null) ?? null,
      approvedAt: (data['approvedAt'] as Timestamp)?.toDate() ?? null,
      reimbursedAt: (data['reimbursedAt'] as Timestamp)?.toDate() ?? null,
      rejectionReason: (data['rejectionReason'] as string | null) ?? null,
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
      updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private async getEmployeeIdForUser(): Promise<string | null> {
    const employeesRef = collection(this.firestore, `tenants/${this.tenantId}/employees`);
    const primary = query(employeesRef, where('email', '==', this.userEmail));
    const snap = await getDocs(primary);
    const docSnap = snap.docs[0];
    if (docSnap) return docSnap.id;

    const fallback = query(employeesRef, where('email', '==', this.authStore.user()?.email ?? ''));
    const fallbackSnap = await getDocs(fallback);
    return fallbackSnap.docs[0]?.id ?? null;
  }

  private async getManagerEmployeeId(): Promise<string | null> {
    const employeeId = await this.getEmployeeIdForUser();
    if (!employeeId) return null;
    const ref = doc(this.firestore, `tenants/${this.tenantId}/employees/${employeeId}`);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return (data['managerId'] as string | null) ?? null;
  }

  private async backfillApprovalsForManager(
    docs: Array<{ id: string; data: () => Record<string, unknown>; ref: DocumentReference }>,
    managerEmployeeId: string
  ): Promise<ExpenseClaim[]> {
    const cache = new Map<string, { managerId: string | null } | null>();
    const matched: ExpenseClaim[] = [];
    const batch = writeBatch(this.firestore);
    let hasUpdates = false;

    for (const docSnap of docs) {
      const data = docSnap.data();
      const userId = data['userId'] as string | undefined;
      if (!userId) continue;

      let record = cache.get(userId);
      if (record === undefined) {
        record = await this.getEmployeeRecordForUserId(userId);
        cache.set(userId, record);
      }

      if (record?.managerId === managerEmployeeId) {
        matched.push(this.mapClaim(docSnap.id, data));
        batch.update(docSnap.ref, {
          approverEmployeeId: managerEmployeeId,
          updatedAt: serverTimestamp(),
        });
        hasUpdates = true;
      }
    }

    if (hasUpdates) {
      await batch.commit();
    }

    return matched;
  }

  private async reconcileExpensesForManager(
    managerEmployeeId: string
  ): Promise<ExpenseClaim[]> {
    try {
      const ref = collection(this.firestore, `tenants/${this.tenantId}/expenseClaims`);
      const snap = await getDocs(
        query(ref, where('status', '==', 'submitted'), orderBy('createdAt', 'desc'))
      );
      if (snap.empty) return [];

      const cache = new Map<string, { managerId: string | null } | null>();
      const matched: ExpenseClaim[] = [];
      const batch = writeBatch(this.firestore);
      let hasUpdates = false;

      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        const userId = data['userId'] as string | undefined;
        if (!userId) continue;

        let record = cache.get(userId);
        if (record === undefined) {
          record = await this.getEmployeeRecordForUserId(userId);
          cache.set(userId, record);
        }

        const currentApprover = (data['approverEmployeeId'] as string | null) ?? null;
        if (record?.managerId === managerEmployeeId && currentApprover !== managerEmployeeId) {
          matched.push(this.mapClaim(docSnap.id, data));
          batch.update(docSnap.ref, {
            approverEmployeeId: managerEmployeeId,
            updatedAt: serverTimestamp(),
          });
          hasUpdates = true;
        }
      }

      if (hasUpdates) {
        await batch.commit();
      }

      return matched;
    } catch (error) {
      console.error('Failed to reconcile expenses for manager:', error);
      return [];
    }
  }

  private async getEmployeeRecordForUserId(
    userId: string
  ): Promise<{ managerId: string | null } | null> {
    try {
      const userRef = doc(this.firestore, `tenants/${this.tenantId}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return null;

      const rawEmail = (userSnap.data()['email'] as string | undefined) ?? '';
      if (!rawEmail) return null;
      const normalizedEmail = rawEmail.trim().toLowerCase();

      const employeesRef = collection(this.firestore, `tenants/${this.tenantId}/employees`);
      let employeeSnap = await getDocs(
        query(employeesRef, where('email', '==', normalizedEmail))
      );
      if (!employeeSnap.docs[0] && rawEmail !== normalizedEmail) {
        employeeSnap = await getDocs(query(employeesRef, where('email', '==', rawEmail)));
      }
      const employeeDoc = employeeSnap.docs[0];
      if (!employeeDoc) return null;
      const data = employeeDoc.data();
      return { managerId: (data['managerId'] as string | null) ?? null };
    } catch (error) {
      console.error('Failed to resolve employee for user:', error);
      return null;
    }
  }
}
